import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  listDashboards,
  getDashboard,
  type Dashboard,
  type DashboardSummary,
} from '../api/dashboards';
import {
  getPost,
  listStoryBlockTypes,
  openDraft,
  renderPost,
  savePostBlocks,
  submitPost,
  uploadStoryImage,
  withdrawPost,
  type Post,
  type RenderedStoryBlock,
  type StoryBlock,
  type StoryBlockType,
  type StoryBlockTypeInfo,
} from '../api/posts';
import { StoryBody } from '../components/posts/StoryBody';
import { ModerationNotice } from '../components/posts/ModerationNotice';
import { StoryStanding } from '../components/posts/StoryStanding';
import { StoryTopics } from '../components/posts/StoryTopics';
import { AppShell } from '../components/layout/AppShell';
import { Alert, Button, Card, ErrorState, Input, LoadingState } from '../components/ui';
import './StoryEditPage.css';

type State = 'loading' | 'ready' | 'error';

/** Which dashboard block types each kind of figure may point at. */
const EMBED_TARGETS: Record<string, string[]> = {
  chart: ['line', 'area', 'bar', 'pie', 'donut', 'scatter', 'histogram', 'box', 'heatmap'],
  kpi: ['kpi', 'statistic'],
  table: ['table'],
};

function newId(): string {
  return `b_${Math.random().toString(36).slice(2, 9)}`;
}

/** `/stories/:id/edit` — writing the story. */
export function StoryEditPage() {
  const { postId = '' } = useParams();

  const [state, setState] = useState<State>('loading');
  const [post, setPost] = useState<Post | null>(null);
  const [blocks, setBlocks] = useState<StoryBlock[]>([]);
  const [types, setTypes] = useState<StoryBlockTypeInfo[]>([]);
  const [dashboards, setDashboards] = useState<DashboardSummary[]>([]);
  const [chosen, setChosen] = useState<Dashboard | null>(null);
  // The blocks of every dashboard this story already borrows from, so a figure
  // that is already set shows what it points at rather than "choose a figure".
  const [known, setKnown] = useState<Record<string, Dashboard>>({});
  const [adding, setAdding] = useState<StoryBlockType>('rich_text');
  const [rendered, setRendered] = useState<Record<string, RenderedStoryBlock>>({});
  const [problems, setProblems] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      const [standing, catalogue, mine] = await Promise.all([
        getPost(postId),
        listStoryBlockTypes(),
        listDashboards(),
      ]);
      // A published story is not an edit buffer: what the editor opens is the
      // draft, and when there is none the page offers to start one rather than
      // letting anyone type over what people are reading.
      const loaded = standing.draft_version === null ? standing : await getPost(postId, 'draft');

      setPost(loaded);
      setBlocks(loaded.blocks);
      setTypes(catalogue);
      setDashboards(mine);
      setState('ready');

      const borrowed = await Promise.all(
        loaded.dashboards.map(async (reference) => {
          try {
            return await getDashboard(reference);
          } catch {
            return null;
          }
        }),
      );
      setKnown(
        Object.fromEntries(
          borrowed
            .filter((entry): entry is Dashboard => entry !== null)
            .map((entry) => [entry.id, entry]),
        ),
      );

      const answer = await renderPost(loaded.id, loaded.version?.number);
      setRendered(Object.fromEntries(answer.blocks.map((block) => [block.id, block])));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, [postId]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = (next: StoryBlock[]) => {
    setBlocks(next);
    setDirty(true);
  };

  const add = (block: StoryBlock) => change([...blocks, block]);

  const update = (id: string, content: StoryBlock['content']) =>
    change(blocks.map((block) => (block.id === id ? { ...block, content } : block)));

  const move = (index: number, by: number) => {
    const target = index + by;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    change(next);
  };

  const remove = (id: string) => change(blocks.filter((block) => block.id !== id));

  const save = async () => {
    if (!post) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await savePostBlocks(post.id, blocks);
      setPost(saved);
      setBlocks(saved.blocks);
      setProblems(saved.reading_problems ?? []);
      setDirty(false);
      const answer = await renderPost(saved.id, saved.version?.number);
      setRendered(Object.fromEntries(answer.blocks.map((block) => [block.id, block])));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setSaving(false);
    }
  };

  const addOfType = async (type: StoryBlockType) => {
    if (type === 'divider') {
      add({ id: newId(), type, content: {} });
      return;
    }
    if (type === 'title' || type === 'subtitle' || type === 'rich_text' || type === 'quote') {
      add({ id: newId(), type, content: { text: '' } });
      return;
    }
    if (type === 'image') {
      add({ id: newId(), type, content: { image: '', alt: '' } });
      return;
    }
    // A figure needs a dashboard chosen before it can name a block of one.
    add({ id: newId(), type, content: { dashboard: chosen?.id ?? '', block: '' } });
  };

  const chooseDashboard = async (id: string) => {
    if (!id) {
      setChosen(null);
      return;
    }
    try {
      const dashboard = await getDashboard(id);
      setChosen(dashboard);
      setKnown((current) => ({ ...current, [dashboard.id]: dashboard }));
    } catch {
      setChosen(null);
    }
  };

  /** The figures a block can choose from: its own dashboard's, or the chosen one's. */
  const figuresFor = (block: StoryBlock) => {
    const source = known[block.content.dashboard ?? ''] ?? chosen;
    return (source?.blocks ?? []).filter((entry) =>
      EMBED_TARGETS[block.type]?.includes(entry.type),
    );
  };

  /** Start, hand over, or take back a version. Each one reloads the page. */
  const act = async (action: (id: string) => Promise<Post>) => {
    if (!post) return;
    setWorking(true);
    setError(null);
    try {
      await action(post.id);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking(false);
    }
  };

  const uploadImage = async (blockId: string, file: File) => {
    if (!post) return;
    try {
      const image = await uploadStoryImage(post.id, file);
      const block = blocks.find((entry) => entry.id === blockId);
      update(blockId, { ...(block?.content ?? {}), image: image.id });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    }
  };

  if (state === 'loading') {
    return (
      <AppShell>
        <LoadingState label="Opening the editor…" />
      </AppShell>
    );
  }

  if (state === 'error' || !post) {
    return (
      <AppShell>
        <ErrorState
          title="We could not open this story"
          description={error?.message}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  // The whole editor hangs off this: a story is editable exactly when it has a
  // draft open, and never because of what it looks like on screen.
  const editing = post.draft_version !== null;

  return (
    <AppShell
      header={
        <div className="sc-page-header">
          <div>
            <h1>{post.title}</h1>
            <p>
              {editing
                ? `Draft version ${post.draft_version}`
                : `Version ${post.version?.number ?? 1}`}{' '}
              · {blocks.length} block{blocks.length === 1 ? '' : 's'}
              {dirty ? ' · unsaved changes' : ''}
            </p>
          </div>
          <div className="sc-page-header__actions">
            <Link to={`/stories/${post.id}`}>Read it</Link>
            {editing && (
              <Button size="sm" isLoading={saving} disabled={!dirty} onClick={() => void save()}>
                Save
              </Button>
            )}
          </div>
        </div>
      }
    >
      <ModerationNotice post={post} />

      {error && (
        <Alert tone="warning" title="That change could not be saved">
          {error.message}
        </Alert>
      )}

      {problems.length > 0 && (
        <Alert tone="info" title="Worth a look before anyone reads this">
          <ul className="sc-editor__problems">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Card
        title="Where this story stands"
        description="A published version is never rewritten. Editing one starts the next."
      >
        <StoryStanding post={post} />
        <div className="sc-editor__life">
          {!editing && post.review_version === null && (
            <Button size="sm" isLoading={working} onClick={() => void act(openDraft)}>
              {post.published_version === null ? 'Keep writing' : 'Start a new draft'}
            </Button>
          )}
          {editing && (
            <Button
              size="sm"
              variant="secondary"
              isLoading={working}
              disabled={dirty || blocks.length === 0}
              onClick={() => void act(submitPost)}
            >
              Publish this version
            </Button>
          )}
          {post.review_version !== null && (
            <Button
              size="sm"
              variant="secondary"
              isLoading={working}
              onClick={() => void act(withdrawPost)}
            >
              Withdraw from review
            </Button>
          )}
          {editing && dirty && (
            <span className="sc-editor__hint">Save your changes before publishing them.</span>
          )}
        </div>
      </Card>

      {!editing && post.review_version !== null && (
        <Alert tone="info" title={`Version ${post.review_version} is waiting for review`}>
          Nothing can be changed while it waits. Withdraw it to keep writing — whatever is published
          stays up either way.
        </Alert>
      )}

      {!editing && post.review_version === null && (
        <Alert tone="info" title="This is the version people read">
          Nothing here can be typed over. Start a new draft to write the next version — the one on
          screen stays up until you publish the new one.
        </Alert>
      )}

      {editing && (
        <Card
          title="Add to the story"
          description="A story is words with figures in them, not figures with words around them."
        >
          <div className="sc-editor__add">
            <label className="sc-editor__field">
              <span>Block</span>
              <select
                value={adding}
                onChange={(event) => setAdding(event.target.value as StoryBlockType)}
              >
                {types.map((type) => (
                  <option key={type.key} value={type.key}>
                    {type.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="sc-editor__field">
              <span>From dashboard</span>
              <select
                value={chosen?.id ?? ''}
                onChange={(event) => void chooseDashboard(event.target.value)}
              >
                <option value="">Choose a dashboard…</option>
                {dashboards.map((dashboard) => (
                  <option key={dashboard.id} value={dashboard.id}>
                    {dashboard.title}
                  </option>
                ))}
              </select>
            </label>

            <Button size="sm" onClick={() => void addOfType(adding)}>
              Add
            </Button>
          </div>
          <p className="sc-editor__hint">
            {types.find((type) => type.key === adding)?.description}
          </p>
        </Card>
      )}

      <div className="sc-editor__blocks">
        {editing &&
          blocks.map((block, index) => (
            <div key={block.id} className="sc-editor__block">
              <div className="sc-editor__toolbar">
                <span className="sc-editor__type">{block.type.replace('_', ' ')}</span>
                <span className="sc-editor__spacer" />
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Move earlier"
                  title="Move earlier"
                  onClick={() => move(index, -1)}
                >
                  ↑
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Move later"
                  title="Move later"
                  onClick={() => move(index, 1)}
                >
                  ↓
                </Button>
                <Button size="sm" variant="danger" onClick={() => remove(block.id)}>
                  Remove
                </Button>
              </div>

              {(block.type === 'title' ||
                block.type === 'subtitle' ||
                block.type === 'rich_text' ||
                block.type === 'quote') && (
                <label className="sc-editor__field">
                  <span className="sc-visually-hidden">Text</span>
                  <textarea
                    aria-label={`${block.type.replace('_', ' ')} text`}
                    rows={block.type === 'rich_text' ? 4 : 2}
                    value={block.content.text ?? ''}
                    onChange={(event) =>
                      update(block.id, { ...block.content, text: event.target.value })
                    }
                  />
                </label>
              )}

              {block.type === 'quote' && (
                <Input
                  label="Who said it"
                  value={block.content.attribution ?? ''}
                  onChange={(event) =>
                    update(block.id, { ...block.content, attribution: event.target.value })
                  }
                />
              )}

              {block.type === 'image' && (
                <>
                  <label className="sc-editor__field">
                    <span>Picture</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) void uploadImage(block.id, file);
                      }}
                    />
                  </label>
                  <Input
                    label="Describe it for someone who cannot see it"
                    value={block.content.alt ?? ''}
                    onChange={(event) =>
                      update(block.id, { ...block.content, alt: event.target.value })
                    }
                  />
                </>
              )}

              {(block.type === 'chart' || block.type === 'kpi' || block.type === 'table') && (
                <div className="sc-editor__embed">
                  <label className="sc-editor__field">
                    <span>Figure</span>
                    <select
                      aria-label="Figure"
                      value={block.content.block ?? ''}
                      onChange={(event) =>
                        update(block.id, {
                          ...block.content,
                          dashboard: chosen?.id ?? block.content.dashboard,
                          block: event.target.value,
                        })
                      }
                    >
                      <option value="">Choose a figure…</option>
                      {figuresFor(block).map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {entry.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Input
                    label="Caption"
                    value={block.content.caption ?? ''}
                    onChange={(event) =>
                      update(block.id, { ...block.content, caption: event.target.value })
                    }
                  />
                </div>
              )}

              {block.type === 'dashboard' && (
                <Input
                  label="Caption"
                  value={block.content.caption ?? ''}
                  onChange={(event) =>
                    update(block.id, { ...block.content, caption: event.target.value })
                  }
                />
              )}
            </div>
          ))}
      </div>

      <StoryTopics post={post} onChanged={setPost} />

      <Card title="How it reads" description="Exactly what someone opening the story would see.">
        <StoryBody post={{ ...post, blocks }} rendered={rendered} />
      </Card>
    </AppShell>
  );
}
